import { createContext, useContext, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { auth } from '../config/firebase';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from 'src/config/firebase';
import { getTotalCreditsForUser, addCreditsForUser } from 'src/services/creditService';
import profileService from 'src/services/profileService';
import { EVENTO_MODULOS } from 'src/utils/sorbyIa/modulosPrueba';

const HANDLERS = {
  INITIALIZE: 'INITIALIZE',
  SIGN_OUT: 'SIGN_OUT',
  UPDATE_USER: 'UPDATE_USER',
};

const initialState = {
  isAuthenticated: false,
  isLoading: true,
  authReady: false,
  user: null,
  originalUser: null,
};

const handlers = {
  [HANDLERS.INITIALIZE]: (state, action) => {
    const payload = action.payload;
    const hasUserField =
      payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'user');
    const hasOriginalUserField =
      payload &&
      typeof payload === 'object' &&
      Object.prototype.hasOwnProperty.call(payload, 'originalUser');
    const hasClearStorageField =
      payload &&
      typeof payload === 'object' &&
      Object.prototype.hasOwnProperty.call(payload, 'clearStorage');

    const user = hasUserField ? payload.user : payload || null;
    const originalUser = hasOriginalUserField ? payload.originalUser : state.originalUser || user;
    const clearStorage = hasClearStorageField ? !!payload.clearStorage : false;

    if (!user) {
      if (clearStorage) {
        window.localStorage.removeItem('MY_APP_STATE');
        window.localStorage.removeItem('authToken');
      }
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        originalUser: null,
      };
    }

    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      user,
      originalUser,
    };
  },
  [HANDLERS.UPDATE_USER]: (state, action) => {
    const user = action.payload?.user || action.payload;
    const originalUser = action.payload?.originalUser || state.originalUser || user;
    const newState = {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      user,
      originalUser,
    };
    window.localStorage.setItem('MY_APP_STATE', JSON.stringify(newState));
    // El token de auth es SIEMPRE el del usuario original (Firebase). Al espiar,
    // el payload del espiado no trae token: no pisar el guardado con undefined.
    const tokenOriginal = originalUser?.token || user?.token;
    if (typeof tokenOriginal === 'string' && tokenOriginal) {
      window.localStorage.setItem('authToken', tokenOriginal);
    }

    return newState;
  },
  [HANDLERS.SIGN_OUT]: (state) => {
    window.localStorage.removeItem('MY_APP_STATE');

    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({ undefined });

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [authReady, setAuthReady] = useState(false);
  const authReadyRef = useRef(false);
  const initialized = useRef(false);
  const stateRef = useRef(initialState);
  // true mientras la sesión en memoria viene de localStorage y Firebase todavía
  // no la confirmó en esta pestaña. Si Firebase responde "sin sesión" estando en
  // provisional, hay que limpiar y mandar al login (sesión fantasma).
  const provisionalRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const initialize = async () => {
    if (initialized.current) {
      return;
    }

    initialized.current = true;

    // 1) Restaurar sesión de localStorage ANTES de esperar a Firebase → UI optimista
    //    inmediata. Es provisional: onAuthStateChanged la confirma o la descarta.
    //    Si lo guardado no parsea o no tiene la forma mínima esperada, se borra
    //    (un valor corrupto no debe sobrevivir y re-fallar en cada carga).
    const storageState = window.localStorage.getItem('MY_APP_STATE');
    if (storageState) {
      try {
        const savedState = JSON.parse(storageState);
        const u = savedState?.user;
        const esValido = u && typeof u === 'object' && (u.id || u.user_id) && u.token;
        if (esValido) {
          provisionalRef.current = true;
          dispatch({
            type: HANDLERS.UPDATE_USER,
            payload: { user: u, originalUser: savedState.originalUser || u },
          });
        } else {
          console.warn('[Auth] MY_APP_STATE inválido (sin user/id/token), descartando');
          window.localStorage.removeItem('MY_APP_STATE');
          window.localStorage.removeItem('authToken');
        }
      } catch (_) {
        console.warn('[Auth] MY_APP_STATE corrupto (no parsea), descartando');
        window.localStorage.removeItem('MY_APP_STATE');
        window.localStorage.removeItem('authToken');
      }
    }

    // 2) Fallback: si Firebase no responde en 5s, desbloquear la app con lo que haya
    //    en localStorage. Armado ANTES de cualquier espera a Firebase: si el SDK se
    //    cuelga (red lenta, IndexedDB bloqueado), la app igual renderiza a los 5s.
    const authReadyTimeout = setTimeout(() => {
      if (!authReadyRef.current) {
        console.warn('[Auth] Timeout esperando onAuthStateChanged, desbloqueando con estado actual');
        setAuthReady(true);
        authReadyRef.current = true;
        // Forzar isLoading=false. Si hay user en state (restaurado de localStorage) lo mantiene;
        // si no, marca como no autenticado para que la app renderice el login.
        const currentUser = stateRef.current?.user || null;
        dispatch({
          type: HANDLERS.INITIALIZE,
          payload: { user: currentUser, originalUser: stateRef.current?.originalUser || currentUser, clearStorage: false },
        });
      }
    }, 5000);

    onAuthStateChanged(auth, async (user) => {
      console.log('AUTENTICANDO CON CREDENCIALES');

      clearTimeout(authReadyTimeout);

      if (user) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 1500;
        let lastError;

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            const idToken = await user.getIdToken(attempt === 1);
            const freshAdmin = await getPayloadUserByUid(user.uid, idToken);

            const current = stateRef.current;
            const spying =
              !!current.user &&
              !!current.originalUser &&
              current.user.id !== current.originalUser.id;

            if (spying) {
              dispatch({
                type: HANDLERS.UPDATE_USER,
                payload: {
                  user: { ...current.user, token: idToken },
                  originalUser: freshAdmin,
                },
              });
            } else {
              dispatch({ type: HANDLERS.UPDATE_USER, payload: freshAdmin });
            }
            provisionalRef.current = false; // sesión confirmada por Firebase + backend
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            if (attempt < MAX_RETRIES) {
              console.warn(`[Auth] Error al cargar perfil (intento ${attempt}/${MAX_RETRIES}), reintentando...`, error);
              await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
            }
          }
        }

        if (lastError) {
          console.error('[Auth] Error al cargar perfil tras todos los intentos:', lastError);
          dispatch({ type: HANDLERS.INITIALIZE, payload: { user: null, originalUser: null, clearStorage: false } });
        }
      } else {
        // Firebase dice "sin sesión". Si lo que tenemos en memoria vino de
        // localStorage y nunca fue confirmado en esta pestaña (provisional),
        // es una sesión fantasma (token revocado / logout en otro lado):
        // limpiar storage y mandar al login. Solo ignoramos el evento null
        // cuando la sesión ya fue confirmada por Firebase en esta pestaña.
        const shouldIgnoreNullEvent =
          !provisionalRef.current &&
          stateRef.current.isAuthenticated &&
          (!!stateRef.current.user?.id || !!stateRef.current.user?.user_id);
        if (shouldIgnoreNullEvent) {
          setAuthReady(true);
          authReadyRef.current = true;
          return;
        }
        provisionalRef.current = false;
        dispatch({
          type: HANDLERS.INITIALIZE,
          payload: { user: null, originalUser: null, clearStorage: true },
        });
      }
      setAuthReady(true);
      authReadyRef.current = true;
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  // Refresh automático del usuario cada 30 minutos (transparente)
  useEffect(() => {
    const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutos

    const refreshUserData = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || !state.isAuthenticated) return;

      try {
        console.log('🔄 [Auth] Refrescando datos de usuario...');
        const idToken = await currentUser.getIdToken(true);
        const freshAdmin = await getPayloadUserByUid(currentUser.uid, idToken);

        const current = stateRef.current;
        const spying =
          !!current.user &&
          !!current.originalUser &&
          current.user.id !== current.originalUser.id;

        if (spying) {
          dispatch({
            type: HANDLERS.UPDATE_USER,
            payload: {
              user: { ...current.user, token: idToken },
              originalUser: freshAdmin,
            },
          });
        } else {
          dispatch({
            type: HANDLERS.UPDATE_USER,
            payload: { user: freshAdmin, originalUser: freshAdmin },
          });
        }
        console.log('✅ [Auth] Usuario actualizado correctamente');
      } catch (error) {
        console.error('❌ [Auth] Error refrescando usuario:', error);

        // Si el usuario fue eliminado o hay error grave, hacer logout
        if (error.code === 'sorby/deleted-user' || error.code === 'auth/user-not-found') {
          console.warn('⚠️ [Auth] Usuario eliminado o no encontrado, cerrando sesión...');
          firebaseSignOut(auth).then(() => {
            dispatch({ type: HANDLERS.SIGN_OUT });
          });
        }
      }
    };

    const intervalId = setInterval(refreshUserData, REFRESH_INTERVAL);
    // Sorby IA activó o desactivó un módulo: las acciones de la empresa cambiaron, no se espera a la próxima vuelta.
    window.addEventListener(EVENTO_MODULOS, refreshUserData);

    // Cleanup al desmontar
    return () => {
      clearInterval(intervalId);
      window.removeEventListener(EVENTO_MODULOS, refreshUserData);
    };
  }, [state.isAuthenticated, state.originalUser]);

  const getPayloadUserByUid = async (uid, idToken) => {
    const user = await profileService.getProfileByUserId(uid);

    if (!user) {
      const error = new Error('Perfil no encontrado.');
      error.code = 'sorby/deleted-user';
      throw error;
    }

    const credit = await getTotalCreditsForUser(user.id);
    return {
      ...user,
      credit,
      admin: user.admin || false,
      token: idToken,
    };
  };

  const classicSignIn = async (email, password) => {
    try {
      const response = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await response.user.getIdToken(true);
      const payload = await getPayloadUserByUid(response.user.uid, idToken);

      dispatch({
        type: HANDLERS.UPDATE_USER,
        payload: { user: payload, originalUser: payload },
      });
    } catch (error) {
      if (error.code === 'auth/id-token-expired') {
        await refreshToken();
        return classicSignIn(email, password);
      }
      if (error.code === 'sorby/deleted-user') {
        throw new Error('Usuario eliminado por el administrador.');
      }
      console.error('Error in classicSignIn:', error);
      throw error;
    }
  };

  const signOut = () => {
    firebaseSignOut(auth).then(() => {
      dispatch({
        type: HANDLERS.SIGN_OUT,
      });
    });
  };

  const updateUser = async (user) => {
    const updatedUser = await profileService.updateProfile(user.id, user);
    const nextUser = { ...state.user, ...updatedUser };

    dispatch({
      type: HANDLERS.UPDATE_USER,
      payload: nextUser,
    });
  };

  const refreshUser = async (user) => {
    console.log('refreshUser', user);
    const credit = await getTotalCreditsForUser(user.id);
    dispatch({
      type: HANDLERS.UPDATE_USER,
      payload: {
        ...user,
        credit,
      },
    });
  };

  const sendResetPasswordEmail = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const updateUserEmail = async (userId, newEmail) => {
    try {
      if (!auth.currentUser) {
        const e = new Error('No hay usuario logueado.');
        e.code = 'sorby/no-current-user';
        throw e;
      }

      // 1) Cambiar en Firebase Auth
      await updateEmail(auth.currentUser, newEmail);

      // 2) Cambiar en profile API
      await profileService.updateProfile(userId, { email: newEmail });

      // 3) Refrescar estado
      const updatedUser = { ...state.user, email: newEmail };
      dispatch({ type: HANDLERS.UPDATE_USER, payload: updatedUser });

      return true;
    } catch (error) {
      // ⚠️ NO PIERDAS EL CODE: re-lanzá el mismo error
      // (si querés, podés adjuntar un mensaje legible sin pisar code)
      if (!error.message) error.message = 'No se pudo actualizar el email.';
      throw error;
    }
  };

  const changePassword = async (newPassword) => {
    if (!auth.currentUser) {
      const e = new Error('No hay usuario logueado.');
      e.code = 'sorby/no-current-user';
      throw e;
    }
    // Puede lanzar auth/requires-recent-login; re-lanzamos preservando el code
    await updatePassword(auth.currentUser, newPassword);
    return true;
  };

  const reauthenticateUser = async (currentPassword) => {
    if (!auth.currentUser) throw new Error('No hay sesión activa');
    const email = auth.currentUser.email;
    if (!email) throw new Error('No se encontró el email actual del usuario');
    const cred = EmailAuthProvider.credential(email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, cred);
  };

  const updateAvatar = async (user, avatarFile) => {
    const filesFolderRef = ref(storage, `avatars/${avatarFile.name}`);
    await uploadBytes(filesFolderRef, avatarFile);
    const downloadURL = await getDownloadURL(filesFolderRef);

    const userUpdated = {
      ...user,
      avatar: downloadURL,
    };
    await updateUser(userUpdated);
  };

  const refreshToken = async () => {
    try {
      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken(true); // Forzar la renovación del token
      const user = {
        ...state.user,
        token: idToken,
      };
      dispatch({
        type: HANDLERS.UPDATE_USER,
        payload: user,
      });
      window.localStorage.setItem('authToken', idToken); // Actualizar el token en localStorage
      return idToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  };

  const spyUser = async (user) => {
    try {
      if (!state.originalUser?.admin) {
        throw new Error('Solo los administradores pueden espiar cuentas');
      }

      const targetUserData = await profileService.getProfileById(user.id);

      if (!targetUserData) {
        throw new Error('Usuario no encontrado');
      }

      const credit = await getTotalCreditsForUser(user.id);

      dispatch({
        type: HANDLERS.UPDATE_USER,
        payload: {
          ...targetUserData,
          credit,
          admin: targetUserData.admin || false,
        },
      });
    } catch (error) {
      console.error('Error spying user:', error);
      throw error;
    }
  };

  const returnToOriginalUser = () => {
    if (!state.originalUser) return;
    dispatch({
      type: HANDLERS.UPDATE_USER,
      payload: { user: state.originalUser, originalUser: state.originalUser },
    });
    window.location.reload();
  };

  const isSpying = () => {
    return !!state.user && !!state.originalUser && state.user.email !== state.originalUser.email;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        authReady,
        classicSignIn,
        signOut,
        updateUser,
        updateAvatar,
        refreshUser,
        sendResetPasswordEmail,
        updateUserEmail,
        changePassword,
        reauthenticateUser,
        spyUser,
        returnToOriginalUser,
        isSpying,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const AuthConsumer = AuthContext.Consumer;

export const useAuthContext = () => useContext(AuthContext);
